import {
  Avatar,
  Box,
  LinearProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  Typography,
  useMediaQuery,
  Theme,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import FiberManualRecordIcon from "@material-ui/icons/FiberManualRecord";
import { FC } from "react";
import { useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";

import useCurrentUser from "../../../hooks/useCurrentUser";
import { GenericFunction, GenericObject } from "../../../types/generic.types";
import { ReduxState } from "../../../types/redux.types";
import { AppResource } from "../../../types/resource.types";
import { truncateText } from "../../../utils/generic";
import { IChatParams, IRoomHeader } from "../type";

const useStyles = makeStyles((theme) => ({
  root: {
    height: "100%",
    backgroundColor: theme.palette.grey[200],
    padding: 0,
    overflow: "auto",
    position: "relative",
  },
  notification: {
    color: theme.palette.error.main,
  },
}));

const ChatRooms: FC<ChatRoomsProps> = ({ setChatResponsive, getClientDetails }) => {
  const classes = useStyles({});
  const { push } = useHistory();
  const { roomId } = useParams<IChatParams>();

  const roomsHeader = useSelector<ReduxState, IRoomHeader[]>((state) => state.chat.roomsHeader);
  const rooms = useSelector<ReduxState, GenericObject>((state) => state.chat.rooms);
  const loadingRooms = useSelector<ReduxState, boolean>((state) => state.chat.loadingRooms);
  const isSm = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));

  const { user } = useCurrentUser();

  const roomHasBullet = (room: any): boolean => {
    return room.participants.includes(user.id) && !room.seenBy[user.id!] && room.id !== roomId;
  };

  const handleClickRoom = (roomHeader: IRoomHeader, name: string, photoURL: string) => {
    try {
      // To change url with selected user chat
      push(`/${AppResource.chat}/${roomHeader.roomId}`);
      if (isSm && setChatResponsive && getClientDetails) {
        getClientDetails(name, photoURL);
        setChatResponsive(true);
      }
    } catch {}
  };

  return (
    <List className={classes.root}>
      {loadingRooms ? (
        <Box display="flex" justifyContent="center" position="absolute" width="100%" top={0}>
          <LinearProgress color="primary" style={{ width: "100%" }} />
        </Box>
      ) : null}
      {roomsHeader.map((roomHeader: IRoomHeader) => {
        return (
          <ListItem
            key={roomHeader.roomId}
            button
            onClick={() =>
              handleClickRoom(
                roomHeader,
                rooms[roomHeader.roomId].client?.displayName,
                rooms[roomHeader.roomId].client?.photoURL
              )
            }
            selected={roomHeader.roomId === roomId}
          >
            <ListItemAvatar>
              <Avatar
                src={rooms[roomHeader.roomId].client?.photoURL}
                alt={rooms[roomHeader.roomId].client?.displayName}
              />
            </ListItemAvatar>
            <ListItemText
              primary={<Typography>{truncateText(rooms[roomHeader.roomId].client?.displayName)}</Typography>}
              secondary={
                <Typography variant="subtitle2" color="textSecondary">
                  {rooms[roomHeader.roomId].lastMessage?.text
                    ? truncateText(rooms[roomHeader.roomId].lastMessage?.text)
                    : "Attachment"}
                </Typography>
              }
            />
            <ListItemSecondaryAction>
              {roomHasBullet(rooms[roomHeader.roomId]) && (
                <Box color="red" fontSize={15}>
                  <FiberManualRecordIcon fontSize="inherit" className={classes.notification} />
                </Box>
              )}
            </ListItemSecondaryAction>
          </ListItem>
        );
      })}
    </List>
  );
};

interface ChatRoomsProps {
  setChatResponsive?: GenericFunction;
  getClientDetails?: GenericFunction;
}

export default ChatRooms;
